// src/BetweenDateTimeInput.js
import React, { useState } from 'react';
import { DateTime } from 'luxon';

export default function BetweenDateTimeInput({ onChange }) {
  const [mode, setMode] = useState('on');
  const [fromDate, setFromDate] = useState({ day: '', month: '', year: '' });
  const [fromTime, setFromTime] = useState({ hour: '', minute: '' });
  const [toDate, setToDate] = useState({ day: '', month: '', year: '' });
  const [toTime, setToTime] = useState({ hour: '', minute: '' });
  const [errors, setErrors] = useState([]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = [];

    const validateInputs = (date, time) => {
      return date.day && date.month && date.year && time.hour && time.minute;
    };

    const buildDateTime = (date, time) => {
      return DateTime.fromObject({
        day: parseInt(date.day, 10),
        month: parseInt(date.month, 10),
        year: parseInt(date.year, 10),
        hour: parseInt(time.hour, 10),
        minute: parseInt(time.minute, 10),
      }, { zone: 'local' });
    };

    const now = DateTime.local();
    if (!validateInputs(fromDate, fromTime)) {
      errs.push('Enter a complete and valid date and time');
    }

    let fromDT, toDT;
    if (errs.length === 0) {
      fromDT = buildDateTime(fromDate, fromTime);
      if (!fromDT.isValid) {
        errs.push('Enter a valid date and time');
      } else if (fromDT > now) {
        errs.push('Date/time must not be in the future');
      }
    }

    if (mode === 'between') {
      if (!validateInputs(toDate, toTime)) {
        errs.push('Enter a complete and valid To date and time');
      } else {
        toDT = buildDateTime(toDate, toTime);
        if (!toDT.isValid) {
          errs.push('Enter a valid To date and time');
        } else if (toDT > now) {
          errs.push('To date/time must not be in the future');
        }
        if (fromDT && toDT && fromDT > toDT) {
          errs.push('From date/time must be before To date/time');
        }
      }
    }

    setErrors(errs);
    if (errs.length === 0) {
      onChange({ mode, from: fromDT.toISO(), to: mode === 'between' ? toDT.toISO() : null });
    }
  };

  const handleAutoTab = (e, maxLength, nextId) => {
    if (e.target.value.length === maxLength) {
      const next = document.getElementById(nextId);
      if (next) next.focus();
    }
  };

  const renderDateInputs = (prefix, dateState, setDateState) => (
    <div className="govuk-date-input govuk-!-margin-bottom-3" id={`${prefix}-date`}>
      <div className="govuk-form-group govuk-date-input__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <label className="govuk-label govuk-date-input__label" htmlFor={`${prefix}-day`}>Date</label>
        <input
          className="govuk-input govuk-date-input__input govuk-input--width-2"
          id={`${prefix}-day`}
          name={`${prefix}-day`}
          inputMode="numeric"
          value={dateState.day}
          onChange={(e) => {
            setDateState({ ...dateState, day: e.target.value });
            handleAutoTab(e, 2, `${prefix}-month`);
          }}
        />
        <span>/</span>
        <input
          className="govuk-input govuk-date-input__input govuk-input--width-2"
          id={`${prefix}-month`}
          name={`${prefix}-month`}
          inputMode="numeric"
          value={dateState.month}
          onChange={(e) => {
            setDateState({ ...dateState, month: e.target.value });
            handleAutoTab(e, 2, `${prefix}-year`);
          }}
        />
        <span>/</span>
        <input
          className="govuk-input govuk-date-input__input govuk-input--width-4"
          id={`${prefix}-year`}
          name={`${prefix}-year`}
          inputMode="numeric"
          value={dateState.year}
          onChange={(e) => {
            setDateState({ ...dateState, year: e.target.value });
          }}
        />
      </div>
    </div>
  );

  const renderTimeInputs = (prefix, timeState, setTimeState) => (
    <div className="govuk-form-group govuk-!-margin-bottom-4" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <label className="govuk-label" htmlFor={`${prefix}-hour`}>Time</label>
      <input
        className="govuk-input govuk-input--width-2"
        id={`${prefix}-hour`}
        name={`${prefix}-hour`}
        inputMode="numeric"
        value={timeState.hour}
        onChange={(e) => {
          setTimeState({ ...timeState, hour: e.target.value });
          handleAutoTab(e, 2, `${prefix}-minute`);
        }}
      />
      <span>:</span>
      <input
        className="govuk-input govuk-input--width-2"
        id={`${prefix}-minute`}
        name={`${prefix}-minute`}
        inputMode="numeric"
        value={timeState.minute}
        onChange={(e) => {
          setTimeState({ ...timeState, minute: e.target.value });
        }}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="govuk-form-group">
        <label className="govuk-label" htmlFor="date-mode">Select mode</label>
        <select
          id="date-mode"
          className="govuk-select govuk-select--width-10"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="on">On</option>
          <option value="between">Between</option>
        </select>
      </div>

      <fieldset className="govuk-fieldset">
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
          <h2 className="govuk-fieldset__heading">{mode === 'on' ? 'At' : 'Between'}</h2>
        </legend>
        {renderTimeInputs('from', fromTime, setFromTime)}
        {renderDateInputs('from', fromDate, setFromDate)}
      </fieldset>

      {mode === 'between' && (
        <fieldset className="govuk-fieldset">
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
            <h2 className="govuk-fieldset__heading">To</h2>
          </legend>
          {renderTimeInputs('to', toTime, setToTime)}
          {renderDateInputs('to', toDate, setToDate)}
        </fieldset>
      )}

      {errors.length > 0 && (
        <div role="alert" className="govuk-error-summary">
          <h2 className="govuk-error-summary__title">There is a problem</h2>
          <ul className="govuk-error-summary__list">
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>
      )}

      <button type="submit" className="govuk-button">Submit</button>
    </form>
  );
}
